import React from 'react';
import { Link as RouterLink, useNavigate, useLocation, useSearchParams as useRouterSearchParams } from 'react-router-dom';

/**
 * Drop-in Link adapter that accepts Next.js `href` and maps to React Router `to`.
 */
export const Link = React.forwardRef(function Link({ href, to, children, className, style, onClick, target, rel, replace, ...props }, ref) {
  const destination = href || to || '/';
  const isExternal = typeof destination === 'string' && (/^https?:\/\//.test(destination) || destination.startsWith('mailto:'));

  if (isExternal) {
    return (
      <a
        ref={ref}
        href={destination}
        className={className}
        style={style}
        onClick={onClick}
        target={target || '_blank'}
        rel={rel || 'noopener noreferrer'}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <RouterLink
      ref={ref}
      to={destination}
      className={className}
      style={style}
      onClick={onClick}
      target={target}
      rel={rel}
      replace={replace}
      {...props}
    >
      {children}
    </RouterLink>
  );
});

/**
 * Drop-in Image adapter that supports Next.js image props (fill, priority, sizes).
 */
export function Image({
  src,
  alt = '',
  width,
  height,
  fill = false,
  priority = false,
  className = '',
  style = {},
  sizes,
  unoptimized,
  ...props
}) {
  const combinedStyle = fill
    ? { position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, right: 0, bottom: 0, objectFit: 'cover', ...style }
    : { ...style };

  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={height}
      sizes={sizes}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      className={className}
      style={combinedStyle}
      {...props}
    />
  );
}

/**
 * Next.js navigation hooks shims.
 */
export function useRouter() {
  const navigate = useNavigate();
  return {
    push: (url, options) => navigate(url, options),
    replace: (url, options) => navigate(url, { ...options, replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
  };
}

export function usePathname() {
  const location = useLocation();
  return location.pathname;
}

export function useSearchParams() {
  return useRouterSearchParams();
}

export default Link;
